import * as React from 'react';
import { DateField } from './DateField';
import { TimeField } from './TimeField';
import { cn } from '@/lib/utils';
import { minSameDayEndAfterStart, maxSameDayStart } from '@/lib/datetime';

export { FieldBox } from './FieldBox';
export { FieldSheet } from './FieldSheet';
export { DateField } from './DateField';
export { TimeField } from './TimeField';

function _today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Combined date + time for values in `'YYYY-MM-DDTHH:mm'` form (replaces native
 * `datetime-local`). Renders an aligned 2-column DateField + TimeField (S-8).
 */
export function DateTimeField({
  value = '',
  onChange,
  minDate,
  minTime,
  stepMinutes = 30,
  dateLabel,
  timeLabel,
  testId,
  className,
}) {
  const d = (value || '').slice(0, 10);
  const tm = (value || '').slice(11, 16);
  return (
    <div className={cn('grid grid-cols-2 gap-2', className)}>
      <DateField
        label={dateLabel}
        size={dateLabel ? 'lg' : 'md'}
        value={d}
        min={minDate}
        onChange={(v) => onChange(`${v}T${tm || '09:00'}`)}
        testId={testId ? `${testId}-date` : undefined}
      />
      <TimeField
        label={timeLabel}
        size={timeLabel ? 'lg' : 'md'}
        value={tm}
        minTime={minTime}
        stepMinutes={stepMinutes}
        onChange={(v) => onChange(`${d || minDate || _today()}T${v}`)}
        testId={testId ? `${testId}-time` : undefined}
      />
    </div>
  );
}

function _hhmmToMin(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/**
 * Paired start–end time range using compact TimeFields with an em-dash between.
 * End chips are always strictly after start (same calendar day).
 * Keeps dense availability rows harmonious (S-8 spacing rule).
 */
export function TimeRange({
  start,
  end,
  onStartChange,
  onEndChange,
  stepMinutes = 30,
  minTime = '00:00',
  maxTime = '23:30',
  disabled = false,
  className,
  testIdPrefix,
}) {
  const startMaxTime = maxSameDayStart(maxTime, stepMinutes);
  const endMinTime = minSameDayEndAfterStart(start, stepMinutes, maxTime);
  const inverted = !!(start && end && _hhmmToMin(end) <= _hhmmToMin(start));

  const handleEndChange = (v) => {
    if (start && _hhmmToMin(v) <= _hhmmToMin(start)) return;
    onEndChange?.(v);
  };

  return (
    <div className={cn('flex items-center gap-2 min-w-0', className)}>
      <TimeField
        value={start}
        onChange={onStartChange}
        stepMinutes={stepMinutes}
        minTime={minTime}
        maxTime={startMaxTime}
        disabled={disabled}
        error={inverted}
        size="md"
        className="w-[104px]"
        testId={testIdPrefix ? `${testIdPrefix}-start` : undefined}
      />
      <span className="text-slate-400 flex-none">—</span>
      <TimeField
        value={end}
        onChange={handleEndChange}
        stepMinutes={stepMinutes}
        minTime={endMinTime}
        maxTime={maxTime}
        disabled={disabled}
        error={inverted}
        size="md"
        className="w-[104px]"
        testId={testIdPrefix ? `${testIdPrefix}-end` : undefined}
      />
    </div>
  );
}
